<template>
  <ValidationObserver ref="form">
    <NotificationControlBar v-if="item"
                            v-model="item"
                            :readOnly="readOnly"
                            @edit-click="toggleReadOnly"
                            @cancel-click="onCancel($event)"
                            v-stream:submit-click="onSubmit$"
                            v-stream:resubmit-click="onResubmit$"
                            :role="role"
                            :deletedBy="user.username"
                            :validate="validate"
                            :title="item.title">
    </NotificationControlBar>
    <div v-if="item">
      <div class="setting-section-block">
        <div class="section-middle">
          <b-container>
            <b-form @submit.prevent>
              <h4 v-if="isAdminNotification">
                最新消息
              </h4>
              <label v-if="isAdminNotification">標題</label>
                <AGCInput
                  v-if="!readOnly"
                  v-model="item.title"
                  name="標題"
                  rules="required|max:50"
                  class="notification mb-3"
                  placeholder="標題"
                  required
                  autofocus
                  :disabled="readOnly"
                ></AGCInput>
                <div v-else class="mb-3 text-secondary">{{item.title}}</div>
                <label v-if="isAdminNotification">內容</label>
                <ContentEditor
                  v-model="item.content"
                  name="內容"
                  class="mb-3"
                  :read-only="readOnly"
                  :toolbar= "toolbarValue"
                ></ContentEditor>
                <div class="datetime" v-if="this.$route.name === 'EditNotification'">
                  <p><span>創建時間：</span>{{item.createdAt}} </p>
                  <p><span>更新時間：</span>{{item.updatedAt}} </p>
                </div>
              </b-form>
          </b-container>
        </div>
      </div>
    </div>
    <ActionSuccessModal v-model="actionSuccessModal"></ActionSuccessModal>
    <LoadingSpinner v-show="loading"></LoadingSpinner>
  </ValidationObserver>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { of } from 'rxjs';
import {
  pluck,
  startWith,
  switchMap,
  tap,
  map,
} from 'rxjs/operators';
import http from '@/helpers/http';
import {
  BForm,
  BContainer,
} from 'bootstrap-vue';
import cloneDeep from 'lodash.clonedeep';
import NotificationControlBar from '@/components/notification/NotificationControlBar.vue';
import imagePath from '@/filters/image-path';
import formMixin from '@/mixins/form';
import AGCInput from '@/components/form/AGCInput.vue';
import ContentEditor from '@/components/editor/ContentEditor.vue';
import ActionSuccessModal from '@/components/ActionSuccessModal.vue';
import { getErrorMsg } from '../../helpers/error-msg';
import toastSettings from '../../helpers/toast-settings';
import LoadingSpinner from '../../components/LoadingSpinner.vue';

const initialState = {
  id: '',
  title: '',
  content: '',
};

export default {
  mixins: [formMixin],
  props: {
    publicApi: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    BForm,
    AGCInput,
    ContentEditor,
    BContainer,
    NotificationControlBar,
    LoadingSpinner,
    ActionSuccessModal,
  },
  data: () => ({
    item: null,
    cropperModal: {
      show: false,
      image: null,
    },
    toolbarValue: [`
      undo redo |
      bold italic underline |
      alignleft aligncenter alignright alignjustify |
      bullist numlist outdent indent |
      link |
    `],
    readOnly: true,
    couponStateBeforeUpdate: null,
    loading: false,
    actionSuccessModal: {
      show: false,
      msg: '成功新增最新消息',
      action: 'REMOVE',
      back: {
        location: '/admin/notification',
        text: '返回最新消息',
      },
    },
  }),
  computed: {
    ...mapGetters(['role']),
    ...mapState(['user']),
    backUrl() {
      return this.role === 'admin' ? '/admin/notification' : null;
    },
    id() {
      return this.$route.params.id;
    },
    apiUrl() {
      return this.role === 'admin' ? '/agc-admin-api/notification/details/' : '/agc-merchant-api/notification/details/';
    },
    hideCheckStatus() {
      if (this.role === 'merchant') {
        return true;
      }
      return false;
    },
    isAdminNotification() {
      return this.role === 'admin' && this.$route.name;
    },
  },
  domStreams: ['onSubmit$', 'onResubmit$'],
  subscriptions() {
    const getData$ = (id) => http({
      method: 'GET',
      url: this.apiUrl + id,
    });

    const submit$ = (data) => http(
      {
        method: 'POST',
        url: `/agc-${this.role}-api/notification/submit`,
        data,
      },
      {
        error: ({ data: { msg } }) => {
          this.$bvToast.toast(getErrorMsg(msg), {
            ...toastSettings,
            title: '提交失敗',
            toaster: 'b-toaster-top-center',
          });
        },
        next: () => {
          this.readOnly = true;
          this.$nextTick(() => {
            this.$refs.form.reset();
          });
          this.actionSuccessModal.show = true;
        },
        complete: () => {
          this.loading = false;
        },
      },
    );

    const resubmit$ = (data) => http(
      {
        method: 'POST',
        url: `/agc-${this.role}-api/notification/resubmit`,
        data,
      },
      {
        error: ({ data: { msg } }) => {
          this.$bvToast.toast(getErrorMsg(msg), {
            ...toastSettings,
            title: '提交失敗',
            toaster: 'b-toaster-top-center',
          });
        },
        next: () => {
          this.readOnly = true;
          this.$nextTick(() => {
            this.$refs.form.reset();
          });
          this.$bvModal.show('coupon-submit-success-modal');
          this.$bvModal.hide('coupon-preview-modal');
        },
        complete: () => {
          this.loading = false;
        },
      },
    );

    return {
      item: this.$watchAsObservable('id').pipe(
        pluck('newValue'),
        startWith(this.id),
        switchMap((id) => {
          if (!id) {
            return of(
              cloneDeep({
                ...initialState,
              }),
            );
          }
          return getData$(id);
        }),
        tap((res) => {
          this.notificationStateBeforeUpdate = cloneDeep(res);
        }),
      ),
      onSubmit: this.onSubmit$.pipe(
        switchMap(() => this.validate({ isScrollToTopIfFail: true })),
        tap(() => {
          this.loading = true;
        }),
        map(() => ({
          ...this.item,
        })),
        switchMap(submit$),
      ),
      onResubmit: this.onResubmit$.pipe(
        switchMap(() => this.validate({ isScrollToTopIfFail: true })),
        tap(() => {
          this.loading = true;
        }),
        map(() => ({
          ...this.item,
        })),
        switchMap(resubmit$),
      ),
    };
  },
  filters: {
    imagePath,
  },
  methods: {
    toggleReadOnly() {
      this.readOnly = !this.readOnly;
    },
    initialize() {
      this.item = cloneDeep({
        ...initialState,
      });
      this.readOnly = false;
    },
    onCancel() {
      this.toggleReadOnly();
      this.$refs.form.reset();
      this.item = cloneDeep({
        ...this.notificationStateBeforeUpdate,
      });
    },
  },
  mounted() {
    if (this.$route.name === 'AddNotification') {
      this.initialize();
    }
  },
};
</script>

<style scoped lang="scss">
.section-header {
  position: sticky;
  background: #fafdff;
  z-index: 30;
}

h4{
  margin-bottom:25px;
}

label {
  font-weight: 500;
  font-size: 15px;
}

.setting-section-block{
  background: #fff;
  box-sizing: border-box;
  border: 1px solid #DDE1E5;
  border-radius: 4px;
  margin: auto;
  overflow: hidden;
  max-width:800px;
  margin-bottom:10px;

  @include respond(phone) {
    margin: 10px 10px;
  }

  & .section-middle{
    width:100%;
    display:inline-block;
    position: relative;
    min-height: 65px;
    padding-top:15px;

    .datetime {
      display: inline-block;
      margin: 80px 0px 10px 0px;
      p {
        width: 100%;
        display: inline-block;
        font-size: 1em;
        margin-bottom: 0.5rem;
      }
        span {
          margin-right: 10px;
        }
    }
  }
}

::v-deep {
  .notification{
    .form-control:disabled {
      background: none;
      border: 0px;
      padding:0px;
      font-weight:500;
    }
  }
}
</style>
